// src/components/Featured.jsx
import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../supabase/axios";
import { toast } from "react-toastify";
import placeholderImg from "../assets/products/coat.jpg";
import { useCurrency } from "../supabase/CurrencyContext";

function Featured() {
  const navigate = useNavigate();

  const {
    currency,              // detected currency (USD/EUR/INR/etc.)
    loading: currencyLoading,
    convertFromINR,
    baseCurrency = "INR",
  } = useCurrency();

  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch top 4 latest products (prices assumed in INR in DB)
  const fetchLatestProducts = async () => {
    try {
      const response = await api.get("/api/products/getLatestProducts");
      setProducts(response.data || []);
    } catch (error) {
      toast.dismiss();
      toast.error(
        error.response?.data?.message || "Failed to fetch latest products"
      );
      setProducts([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (currencyLoading) return; // wait until currency detection + FX ready
    fetchLatestProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, currencyLoading]);

  const SkeletonCard = () => (
    <div className="
    relative
    bg-[#F6F1EE]
    border border-[#D8C9BF]
    rounded-none
    shadow-none
    p-5
    animate-pulse
  ">

      {/* NEW badge skeleton */}
      <div className="
      absolute
      top-4
      left-4
      w-12
      h-5
      bg-[#DCCAC0]
    "></div>

      {/* Luxury product image skeleton */}
      <div className="
      aspect-[3/4]
      overflow-hidden
      mb-6
      bg-[#EEE6E1]
    "></div>

      {/* Product Name */}
      <div className="
      h-4
      bg-[#4B2C20]/15
      w-3/4
      mx-auto
      mb-3
    "></div>

      {/* Price */}
      <div className="
      h-4
      bg-[#4B2C20]/10
      w-1/3
      mx-auto
    "></div>

    </div>
  );

  const showSkeletons =
    isLoading || currencyLoading || products.length === 0;

  // Price formatting helper
  const formatPrice = (value, currencyCode) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      maximumFractionDigits: 2,
    }).format(Number(value || 0));
  };

  return (
    <section className="py-20 px-6 bg-[#F1E7E5] text-center relative overflow-hidden">
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-4xl font-bold mb-3 text-[#4B2C20]"
      >
        Featured Treasures
      </motion.h2>
      <p className="text-[#4B2C20]/70 mb-14 max-w-xl mx-auto">
        Handpicked vintage pieces that capture timeless style and craftsmanship.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
        {showSkeletons ? (
          Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)
        ) : (
          products.map((product, i) => {
            // Assume product.Price & product.old_price are in INR
            const basePriceINR = Number(product.Price || 0);
            const baseOldPriceINR = Number(product.old_price || 0);

            const displayPrice = convertFromINR
              ? convertFromINR(basePriceINR)
              : basePriceINR;

            const displayOldPrice =
              baseOldPriceINR > 0
                ? convertFromINR
                  ? convertFromINR(baseOldPriceINR)
                  : baseOldPriceINR
                : null;

            const showBaseNote =
              currency &&
              baseCurrency &&
              currency !== baseCurrency &&
              basePriceINR > 0;

            return (
              <motion.div
                key={product.ProductId}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.15, duration: 0.6 }}
                whileHover={{ y: -4 }}
                className="group cursor-pointer"
                onClick={() => navigate(`/products/${product.ProductId}`)}
              >

                <div className="
bg-transparent
border-none
overflow-hidden
">

                  {/* IMAGE */}

                  <div className="
aspect-[3/4]
overflow-hidden
bg-[#EDE5E0]
mb-5
">

                    <img
                      src={
                        product.product_images?.find(
                          (img) => img.is_hero === true
                        )?.image_url || placeholderImg
                      }
                      alt={product.Name}
                      className="
w-full
h-full
object-cover
transition-all
duration-700
group-hover:scale-105
"
                    />

                  </div>

                  {/* CONTENT */}

                  <div className="text-center">

                    <h3 className="
uppercase
tracking-[0.20em]
text-[12px]
font-medium
text-[#3A241C]
mb-2
">

                      {product.Name}

                    </h3>

                    <div className="
text-[#4B2C20]
text-lg
font-light
tracking-wide
">

                      {formatPrice(displayPrice, currency || baseCurrency)}

                    </div>

                    {displayOldPrice && (

                      <div className="
text-sm
text-[#6B5A52]
line-through
mt-1
">

                        {formatPrice(displayOldPrice, currency || baseCurrency)}

                      </div>

                    )}

                  </div>

                </div>

              </motion.div>
            );
          })
        )}
      </div>

      <motion.button
        whileTap={{ scale: 0.95 }}
        onClick={() => navigate("/products")}
        className="mt-16 bg-[#4B2C20] text-white px-8 py-3 rounded-full shadow hover:bg-[#6B4226] transition"
      >
        View All Products
      </motion.button>
    </section>
  );
}

export default Featured;
